import { type FC } from 'react';
import { Check, Crown, Minus } from 'lucide-react';
import { ButtonLink } from '@/components/Button/ButtonLink';
import { AI_QUESTIONS, aiAllowanceShort, type Plans } from '@/services/plans';

interface LandingPricingProps {
  plans: Plans;
}

interface Row {
  label: string;
  free: string;
  premium: string;
}

const money = (symbol: string, value: number) => `${symbol}${Number.isInteger(value) ? value : value.toFixed(2)}`;
const count = (value: number) => (value === -1 ? 'Unlimited' : value.toLocaleString('en-GB'));

/** The comparison rows, built from the plan numbers so the page never states a limit the app does not enforce. */
function comparisonRows(plans: Plans): Row[] {
  const { free, premium } = plans;
  return [
    { label: 'Search the rules library', free: 'Unlimited', premium: 'Unlimited' },
    { label: `Ask Grimm (${AI_QUESTIONS} with sources)`, free: aiAllowanceShort(free), premium: `${aiAllowanceShort(premium)}*` },
    { label: 'In-depth answers mid-battle and for list building', free: '', premium: 'Included*' },
    { label: 'Collection and Pile of Potential', free: `${count(free.collectionUnits)} units`, premium: `${count(premium.collectionUnits)}*` },
    { label: 'Rosters', free: count(free.rosters), premium: count(premium.rosters) },
    { label: 'Painting journal photos', free: count(free.journalPhotos), premium: `${count(premium.journalPhotos)}*` },
    { label: 'Game history', free: 'Kept forever', premium: 'Kept forever' },
    { label: 'Clubs, events, dice and friends', free: 'Included', premium: 'Included' },
  ];
}

const Cell: FC<{ value: string }> = ({ value }) =>
  value ? <span>{value}</span> : <><Minus aria-hidden="true" size={16} className="inline text-text-muted" /><span className="sr-only">Not included</span></>;

/**
 * LandingPricing - Free against Premium, annual price first, the Founding Member offer while places remain, and the
 * welcome week. Everything comes from the plans, nothing is hard-coded here.
 */
export const LandingPricing: FC<LandingPricingProps> = ({ plans }) => {
  const { prices } = plans;
  const sym = prices.currencySymbol;
  const rows = comparisonRows(plans);
  return (
    <section id="pricing" aria-labelledby="pricing-heading" className="scroll-mt-20 border-t border-border-subtle bg-bg-secondary/40 py-14">
      <div className="container-app max-w-5xl">
        <h2 id="pricing-heading" className="text-center font-display text-3xl font-bold text-accent-primary">
          Free to play. Premium when you want more Grimm.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-text-secondary">
          Everything you need to run your hobby is free. Premium adds more Grimm, more room for your collection and photos, and keeps an independent
          project going: no ads, and we never sell your data.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-border-subtle bg-bg-secondary p-6">
            <h3 className="font-display text-xl font-semibold text-text-primary">Free</h3>
            <p className="mt-2 text-3xl font-bold text-text-primary">{money(sym, 0)}</p>
            <p className="text-sm text-text-secondary">For as long as you like.</p>
            <ButtonLink to="/register" variant="secondary" size="lg" fullWidth className="mt-5">Create a free account</ButtonLink>
          </div>
          <div className="rounded-lg border-2 border-accent-primary bg-bg-secondary p-6">
            <h3 className="flex items-center gap-2 font-display text-xl font-semibold text-accent-primary">
              <Crown aria-hidden="true" size={20} />
              Premium
            </h3>
            <p className="mt-2 text-3xl font-bold text-text-primary">
              {money(sym, prices.annualPerMonth)} <span className="text-base font-normal text-text-secondary">a month</span>
            </p>
            <p className="text-sm text-text-secondary">
              Billed yearly at {money(sym, prices.annual)}
              {prices.annualSavingPercent > 0 && <> (save {prices.annualSavingPercent}%)</>}, or {money(sym, prices.monthly)} a month.
            </p>
            <ButtonLink to="/register" variant="primary" size="lg" fullWidth className="mt-5">
              {prices.welcomeWeekDays > 0 ? `Start your ${prices.welcomeWeekDays}-day welcome week` : 'Create account'}
            </ButtonLink>
            {prices.welcomeWeekDays > 0 && (
              <p className="mt-2 text-center text-sm text-text-secondary">Try everything free for {prices.welcomeWeekDays} days. No card needed.</p>
            )}
          </div>
        </div>

        {prices.founding && (
          <div data-testid="founding-offer" className="mt-6 rounded-lg border border-accent-primary/40 bg-accent-primary/5 p-5 text-center">
            <p className="font-display text-lg font-semibold text-accent-primary">Founding Member: {money(sym, prices.founding.price)} a year</p>
            <p className="mt-1 text-sm text-text-secondary">
              For the first members only, and the price stays fixed for as long as you stay. {prices.founding.seatsLeft}{' '}
              {prices.founding.seatsLeft === 1 ? 'place' : 'places'} left.
            </p>
          </div>
        )}

        <div className="mt-10 overflow-hidden rounded-lg border border-border-subtle">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">What is included in the Free and Premium plans</caption>
            <thead className="bg-bg-tertiary text-text-primary">
              <tr>
                <th scope="col" className="px-3 py-3 sm:px-4 font-semibold">What you get</th>
                <th scope="col" className="px-3 py-3 sm:px-4 font-semibold">Free</th>
                <th scope="col" className="px-3 py-3 sm:px-4 font-semibold text-accent-primary">Premium</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-border-subtle">
                  <th scope="row" className="px-3 py-3 sm:px-4 font-normal text-text-primary">{row.label}</th>
                  <td className="px-3 py-3 sm:px-4 text-text-secondary"><Cell value={row.free} /></td>
                  <td className="px-3 py-3 sm:px-4 text-text-primary">
                    {row.premium.startsWith('Included') ? <Check aria-hidden="true" size={16} className="mr-1 inline text-accent-primary" /> : null}
                    <Cell value={row.premium} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-text-muted">* {prices.fairUseNote}</p>
      </div>
    </section>
  );
};
